import React from "react";
import { motion } from "framer-motion";

import Header from "../components/layout/Header";
import UserProfile from "../components/dashboard/UserProfile";
import ExerciseTrendWidget from "../components/dashboard/ExerciseTrendWidget";
import ExerciseSnapshotWidget from "../components/dashboard/ExerciseSnapshotWidget";
import ProgressWidget from "../components/dashboard/ProgressWidget";
import MealPlanWidget from "../components/dashboard/MealPlanWidget";

import { useAuth } from "../context/AuthContext";
import { useAthleteTrackingDay } from "../hooks/queries/useAthleteData";
import { useToday } from "../hooks/useToday";
import { getTodayMeals } from "../utils/todayMeals";
import { useNotifications } from "../components/layout/routing/AppShell";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.5, staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const getLatestTrackingRecord = (data) => {
  const records = Array.isArray(data?.athlete_tracking)
    ? [...data.athlete_tracking]
    : [];
  if (records.length === 0) return null;
  records.sort((a, b) => {
    const dateA = new Date(a?.record_date || 0).getTime();
    const dateB = new Date(b?.record_date || 0).getTime();
    return dateB - dateA;
  });
  return records[0];
};

const AthleteDashboardPage = () => {
  const { athleteData } = useAuth();
  const { openNotifications } = useNotifications();

  const latestTracking = getLatestTrackingRecord(athleteData);
  const metrics = latestTracking?.metrics || {};
  // Training and progress are last-known values (each section's newest row);
  // meals are strictly today's, like the Nutrition screen.
  const today = useToday();
  const todayQuery = useAthleteTrackingDay(athleteData?.id, today);
  const todayMeals = getTodayMeals(todayQuery.data?.metrics?.meals);

  return (
    <>
      <Header
        currentView="dashboard"
        displayName={athleteData?.full_name}
        onNotificationsClick={openNotifications}
      />

      <div className="flex-1 flex flex-col lg:flex-row overflow-y-auto lg:overflow-hidden bg-gray-50">
        <main className="flex-1 lg:overflow-y-auto p-4 sm:p-6">
          <motion.div
            className="space-y-6"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants}>
              <UserProfile data={athleteData} />
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="grid grid-cols-1 lg:grid-cols-5 gap-6"
            >
              <div className="lg:col-span-3 flex flex-col gap-4">
                {/* Sin widget de calorías hasta reconstruir la estimación
                    (docs/roadmap/meal-nutrition.md): mostraba una gráfica
                    inventada y una meta fija de 2500 kcal. */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ExerciseTrendWidget data={metrics?.training} />
                  <ExerciseSnapshotWidget data={metrics?.training} />
                </div>
                <MealPlanWidget data={todayMeals} />
              </div>
              <div className="lg:col-span-2">
                <ProgressWidget
                  data={metrics?.progress}
                  trainingData={metrics?.training}
                />
              </div>
            </motion.div>
          </motion.div>
        </main>
      </div>
    </>
  );
};

export default AthleteDashboardPage;
